import { useRef, type CSSProperties } from 'react';
import type { LessonModel } from '../../../Models/LessonModel';
import type { useTranslation } from '../../../i18n/useTranslation';
import { useMediaQuery } from '../../../Utils/useMediaQuery';
import { MoneyText } from '../../SharedArea/MoneyText/MoneyText';
import './WeekView.css';
import { useDisplayName } from '../../../Utils/useDisplayName';

import type { PlannedLesson } from '../../../Utils/PlannedLessonUtil';

export type { PlannedLesson };

/** A booked lesson, with everything the view needs already resolved. */
export interface WeekLesson {
  lesson: LessonModel;
  /** `is-paid`, `is-unpaid`, ... — drives the colour, same vocabulary as everywhere else. */
  visual: string;
  /** Translated up front, so this file holds no status vocabulary of its own. */
  statusLabel: string;
  durationMinutes: number;
}

export interface WeekDayModel {
  /** yyyy-mm-dd */
  key: string;
  number: number;
  weekdayLabel: string;
  isToday: boolean;
  lessons: WeekLesson[];
  planned: PlannedLesson[];
}

interface WeekViewProps {
  days: WeekDayModel[];
  onOpen: (lesson: LessonModel) => void;
  onOpenDay: (date: string) => void;
  onAdd: (item: PlannedLesson) => void;
  tr: ReturnType<typeof useTranslation>['tr'];
}

const FIRST_HOUR = 7;
const LAST_HOUR = 23;
const HOURS = Array.from({ length: LAST_HOUR - FIRST_HOUR }, (_, index) => FIRST_HOUR + index);

/**
 * The week, in whichever shape the screen can actually hold.
 *
 * A seven-column hour grid needs roughly 1,200px to be readable. Below that
 * there are only bad options: squeeze the columns until every student's name
 * is an ellipsis, or scroll sideways, which then has to fight a sticky hour
 * axis for the same edge and lands mid-column on a swipe. The previous
 * version of this view did both, and the names were unreadable either way.
 *
 * So the phone gets a different presentation of the same week rather than a
 * shrunken copy of the desktop one: the days stacked vertically, each lesson
 * on a full-width row where the name has the whole screen and never needs
 * truncating. Nothing scrolls sideways, so none of that class of bug can
 * come back. The hour grid is still the right answer once there is room for
 * it, and takes over at the width where it fits.
 */
export function WeekView({ days, onOpen, onOpenDay, onAdd, tr }: WeekViewProps) {
  // 860px is where the rest of this screen already switches between its
  // compact and full layouts (Calendar.css), and where seven columns stop
  // being cramped.
  const hasRoomForGrid = useMediaQuery('(min-width: 860px)');
  return hasRoomForGrid
    ? <WeekGrid days={days} onOpen={onOpen} onOpenDay={onOpenDay} onAdd={onAdd} tr={tr} />
    : <WeekAgenda days={days} onOpen={onOpen} onOpenDay={onOpenDay} onAdd={onAdd} tr={tr} />;
}

/* ------------------------------------------------------------------ *
 * Phone: the week as a vertical list of days.                          *
 * ------------------------------------------------------------------ */

function WeekAgenda({ days, onOpen, onOpenDay, onAdd, tr }: WeekViewProps) {
  const displayName = useDisplayName();
  const sections = useRef(new Map<string, HTMLElement>());

  /**
   * The strip is the at-a-glance half of a week view: which days are busy,
   * without reading every row. Tapping one jumps to it rather than leaving
   * for the day screen, because the point of being here is to compare days.
   *
   * The smoothness is decided here rather than by CSS `scroll-behavior`: on
   * a phone the element that actually scrolls is the page, not anything this
   * component owns, so there is no one box to put that property on.
   */
  function jumpTo(key: string) {
    const node = sections.current.get(key);
    // jsdom implements no scrolling at all.
    if (typeof node?.scrollIntoView !== 'function') return;
    const still = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    node.scrollIntoView({ block: 'start', behavior: still ? 'auto' : 'smooth' });
  }

  return (
    <div className="cal-panel cal-week cal-week-agenda">
      <nav className="cal-week-strip" aria-label={tr('calendarWeek')}>
        {days.map(day => {
          const total = day.lessons.length + day.planned.length;
          return (
            <button
              key={day.key}
              type="button"
              className={`cal-week-pill${day.isToday ? ' is-today' : ''}${total ? ' has-lessons' : ''}`}
              onClick={() => jumpTo(day.key)}
              aria-label={`${day.weekdayLabel} ${day.number}, ${total > 0 ? tr('calendarDayLoad', total) : tr('calendarNoLessonsShort')}`}
            >
              <span className="cal-week-pill-day">{day.weekdayLabel}</span>
              <span className="cal-week-pill-num"><bdi>{day.number}</bdi></span>
              <span className="cal-week-pill-load" aria-hidden="true">
                {total > 0 ? <bdi>{total}</bdi> : null}
              </span>
            </button>
          );
        })}
      </nav>

      <div className="cal-week-days">
        {days.map(day => {
          const total = day.lessons.length + day.planned.length;
          return (
            <section
              key={day.key}
              className={`cal-week-day${day.isToday ? ' is-today' : ''}`}
              ref={node => {
                // Block body on purpose: React 19 reads a returned value as a
                // ref cleanup function, and Map.set returns the map.
                if (node) sections.current.set(day.key, node);
                else sections.current.delete(day.key);
              }}
            >
              <header className="cal-week-day-head">
                <button type="button" className="cal-week-day-open" onClick={() => onOpenDay(day.key)}>
                  <span className="cal-week-day-name">{day.weekdayLabel}</span>
                  <span className="cal-week-day-num"><bdi>{day.number}</bdi></span>
                </button>
                {total > 0 && <span className="cal-week-day-count"><bdi>{total}</bdi></span>}
              </header>

              {total === 0
                ? <p className="cal-week-day-empty">{tr('calendarNoLessonsShort')}</p>
                : (
                  <ul className="cal-week-day-list">
                    {day.lessons.map(({ lesson, visual, statusLabel }) => (
                      <li key={lesson.id}>
                        <button type="button" className={`cal-week-row ${visual}`} onClick={() => onOpen(lesson)}>
                          <bdi className="cal-week-row-time">{lesson.time ?? tr('calendarNoTime')}</bdi>
                          <span className="cal-week-row-name">{displayName(lesson.student)}</span>
                          <MoneyText amount={lesson.amount} className="cal-week-row-amount" />
                          <span className="cal-week-row-status">{statusLabel}</span>
                        </button>
                      </li>
                    ))}
                    {day.planned.map(item => (
                      <li key={`${item.student}-${item.time}`}>
                        <button type="button" className="cal-week-row is-planned" onClick={() => onAdd(item)}>
                          <bdi className="cal-week-row-time">{item.time}</bdi>
                          <span className="cal-week-row-name">{displayName(item.student)}</span>
                          <span className="cal-week-row-amount" />
                          <span className="cal-week-row-status">{tr('calendarPlanned')}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Desktop: the week as an hour grid.                                   *
 * ------------------------------------------------------------------ */

function WeekGrid({ days, onOpen, onOpenDay, onAdd, tr }: WeekViewProps) {
  const displayName = useDisplayName();
  return (
    <div className="cal-panel cal-week cal-week-grid-scroll">
      <div className="cal-week-grid" style={{ '--cal-week-hours': LAST_HOUR - FIRST_HOUR } as CSSProperties}>
        <div className="cal-week-corner" />
        {days.map(day => (
          <button
            key={`head-${day.key}`}
            type="button"
            className={`cal-week-head${day.isToday ? ' is-today' : ''}`}
            onClick={() => onOpenDay(day.key)}
          >
            <span className="cal-week-head-name">{day.weekdayLabel}</span>
            <span className="cal-week-head-num"><bdi>{day.number}</bdi></span>
          </button>
        ))}

        {/* The columns only ever share the width available, so this axis is a
            plain first column rather than something stuck to a scrolling
            edge. Nothing scrolls horizontally here, which is the whole
            reason this grid is only rendered when it fits. */}
        <div className="cal-week-axis">
          {HOURS.map(hour => (
            <span key={hour} className="cal-week-tick" style={{ top: `${offsetPercent(hour * 60)}%` }}>
              <bdi>{String(hour).padStart(2, '0')}:00</bdi>
            </span>
          ))}
        </div>

        {days.map(day => (
          <div key={day.key} className={`cal-week-col${day.isToday ? ' is-today' : ''}`}>
            {HOURS.map(hour => <i key={hour} className="cal-week-line" style={{ top: `${offsetPercent(hour * 60)}%` }} />)}

            {day.lessons.map(({ lesson, visual, durationMinutes }) => (
              <button
                key={lesson.id}
                type="button"
                className={`cal-week-block ${visual}`}
                style={blockPosition(lesson.time ?? '07:00', durationMinutes)}
                onClick={() => onOpen(lesson)}
                title={displayName(lesson.student)}
              >
                <span className="cal-week-block-name">{displayName(lesson.student)}</span>
                {lesson.time && <bdi className="cal-week-block-time">{lesson.time}</bdi>}
              </button>
            ))}

            {day.planned.map(item => (
              <button
                key={`${item.student}-${item.time}`}
                type="button"
                className="cal-week-block is-planned"
                style={blockPosition(item.time, item.durationMinutes)}
                onClick={() => onAdd(item)}
                title={`${displayName(item.student)} · ${tr('calendarPlanned')}`}
              >
                <span className="cal-week-block-name">{displayName(item.student)}</span>
                <bdi className="cal-week-block-time">{item.time}</bdi>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Where a minute-of-day sits down the grid, as a percentage of the hours shown. */
function offsetPercent(minutes: number): number {
  return (minutes - FIRST_HOUR * 60) / ((LAST_HOUR - FIRST_HOUR) * 60) * 100;
}

function blockPosition(time: string, durationMinutes: number): CSSProperties {
  const top = Math.max(0, offsetPercent(timeMinutes(time)));
  // A floor on the height, or a short lesson renders as an unreadable sliver.
  const height = Math.max(3, durationMinutes / ((LAST_HOUR - FIRST_HOUR) * 60) * 100);
  return { top: `${top}%`, height: `${height}%` };
}

function timeMinutes(time: string): number {
  const [hour, minute] = time.split(':').map(Number);
  return (hour ?? 0) * 60 + (minute ?? 0);
}
